import React from 'react';
import { NormalizedPublicListing } from 'types/listingV3';
import { DynamicListingsGrid } from './DynamicListingsGrid';
import NoResultsPage from './NoResultsPage';

interface ListingsResultsProps {
  /** True on `/search`-like routes — gates the "no results" empty state. On non-results routes
      only the grid renders. */
  readonly isResults: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** Listings prepared for display (already grouped / normalized by the caller). Both the
      grid rendering and the empty-state check use this list — count and content stay in sync. */
  readonly listings: NormalizedPublicListing[];
  readonly loadMore: () => void;
}

/**
 * Shared results-area for the landing pages: the listings grid (which shows its own skeleton
 * loading state internally via <DynamicGridLoading>) and the "no experiences found" empty
 * state when a results query completes with zero hits. The grid renders unconditionally except
 * when a results view has resolved to an empty list — in that single case the empty-state
 * page takes over. Extracted from the four landing-page variants where this block was
 * duplicated verbatim.
 */
const ListingsResults = ({
  isError,
  isLoading,
  isResults,
  listings,
  loadMore,
}: ListingsResultsProps) => {
  const showEmptyState = isResults && !isLoading && listings.length === 0;

  return (
    <>
      {!showEmptyState && (
        <DynamicListingsGrid
          isError={isError}
          isLoading={isLoading}
          listings={listings}
          loadMore={loadMore}
        />
      )}
      {showEmptyState && <NoResultsPage />}
    </>
  );
};

export default ListingsResults;
